/**
 * Invitations admin screen (ADMIN-PR1-a) — fully backed by the live SDK:
 *   - list by status (GET /v1/auth/invitations/?status=)
 *   - create (POST /v1/auth/invitations/)
 *   - revoke (DELETE /v1/auth/invitations/{id})
 */
import { useState, type FormEvent, type ReactNode } from "react";
import type { InvitationRecord, InvitationStatus, OrgRole } from "@oxbo/auth-client";
import { authClient } from "../auth/authClient";
import { errorMessage, useAsync } from "../lib/useAsync";
import { EmptyState, ErrorBanner, PageHeader, Spinner, StatusBadge } from "../components/ui";

const STATUSES: InvitationStatus[] = ["pending", "accepted", "revoked", "expired"];
const ROLES: OrgRole[] = ["owner", "admin", "member"];

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function InvitationsPage(): ReactNode {
  const [status, setStatus] = useState<InvitationStatus>("pending");
  const { data, loading, error, reload } = useAsync<InvitationRecord[]>(
    () => authClient.listInvitations(status),
    [status],
  );

  const [showForm, setShowForm] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  async function onRevoke(id: string) {
    setMutationError(null);
    if (!window.confirm("Revoke this invitation?")) return;
    try {
      await authClient.revokeInvitation(id);
      reload();
    } catch (err) {
      setMutationError(errorMessage(err));
    }
  }

  return (
    <div>
      <PageHeader title="Invitations">
        <button
          type="button"
          onClick={() => setShowForm((s) => !s)}
          className="rounded bg-oxbo px-3 py-1.5 text-sm font-medium text-white hover:bg-oxbo-dark"
        >
          {showForm ? "Close" : "New invitation"}
        </button>
      </PageHeader>

      {showForm && (
        <CreateInvitationForm
          onCreated={() => {
            setShowForm(false);
            reload();
          }}
          onError={setMutationError}
        />
      )}

      <div className="mb-3 flex items-center gap-2">
        <label className="text-sm text-slate-600" htmlFor="inv-status">
          Status
        </label>
        <select
          id="inv-status"
          value={status}
          onChange={(e) => setStatus(e.target.value as InvitationStatus)}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {mutationError && (
        <div className="mb-3">
          <ErrorBanner message={mutationError} />
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorBanner message={errorMessage(error)} />
      ) : !data || data.length === 0 ? (
        <EmptyState message={`No ${status} invitations.`} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Expires</th>
                <th className="px-3 py-2 font-medium">Created</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-3 py-2">{inv.email}</td>
                  <td className="px-3 py-2">{inv.role}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={inv.status} />
                  </td>
                  <td className="px-3 py-2 text-slate-500">{fmtDate(inv.expires_at)}</td>
                  <td className="px-3 py-2 text-slate-500">{fmtDate(inv.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    {inv.status === "pending" && (
                      <button
                        type="button"
                        onClick={() => void onRevoke(inv.id)}
                        className="rounded border border-slate-300 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreateInvitationForm({
  onCreated,
  onError,
}: {
  onCreated: () => void;
  onError: (msg: string) => void;
}): ReactNode {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrgRole>("member");
  const [orgName, setOrgName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    onError("");
    try {
      await authClient.createInvitation({
        email,
        role,
        organization_name: orgName,
        ...(expiresInDays ? { expires_in_days: Number(expiresInDays) } : {}),
        ...(message ? { custom_message: message } : {}),
      });
      onCreated();
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mb-5 grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2"
    >
      <label className="text-sm">
        <span className="mb-1 block text-slate-600">Email</span>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded border border-slate-300 px-2 py-1.5"
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-slate-600">Organization name</span>
        <input
          type="text"
          required
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          className="w-full rounded border border-slate-300 px-2 py-1.5"
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-slate-600">Role</span>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as OrgRole)}
          className="w-full rounded border border-slate-300 px-2 py-1.5"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-slate-600">Expires in (days, optional)</span>
        <input
          type="number"
          min={1}
          value={expiresInDays}
          onChange={(e) => setExpiresInDays(e.target.value)}
          className="w-full rounded border border-slate-300 px-2 py-1.5"
        />
      </label>
      <label className="text-sm sm:col-span-2">
        <span className="mb-1 block text-slate-600">Custom message (optional)</span>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          className="w-full rounded border border-slate-300 px-2 py-1.5"
        />
      </label>
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-oxbo px-4 py-1.5 text-sm font-medium text-white hover:bg-oxbo-dark disabled:opacity-50"
        >
          {busy ? "Sending…" : "Send invitation"}
        </button>
      </div>
    </form>
  );
}
